'use client'
import {
  Typography_default
} from "./chunk-QNSUZHUC.mjs";
import {
  Button_default
} from "./chunk-5TLHZ4PW.mjs";
import {
  __spreadProps,
  __spreadValues
} from "./chunk-UYSR6ZT4.mjs";

// src/RadioGroup/RadioGroup.tsx
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { twMerge } from "tailwind-merge";
import { SuccessIcon } from "@wbk/svg";

// src/RadioGroup/RadioGroup.classes.tsx
var RADIO_GROUP_CLASSES = {
  border: {
    primary: "has-[[data-state=checked]]:border-primary hover:border-primary/70 focus:border-primary-400 border-2 border-paper rounded-S",
    secondary: "has-[[data-state=checked]]:border-secondary hover:border-secondary/70 focus:border-secondary-400 border-2 border-paper rounded-S",
    tertiary: "has-[[data-state=checked]]:border-tertiary hover:border-tertiary/70 focus:border-tertiary-400 border-2 border-paper rounded-S",
    action: "has-[[data-state=checked]]:border-action hover:border-action/70 focus:border-action-400 border-2 border-paper rounded-S",
    white: "has-[[data-state=checked]]:border-body-dark hover:border-body-dark/70 focus:border-body-dark/70 border-2 border-paper rounded-S",
    black: "has-[[data-state=checked]]:border-body-light hover:border-body-light/70 focus:border-body-light/70 border-2 border-paper rounded-S"
  },
  "no-border": {
    primary: "hover:bg-primary/5 rounded-XS",
    secondary: "hover:bg-secondary/5 rounded-XS",
    tertiary: "hover:bg-tertiary/5 rounded-XS",
    action: "hover:bg-action/5 rounded-XS",
    white: "hover:bg-body-dark/5 rounded-XS",
    black: "hover:bg-body-light/5 rounded-XS"
  }
};
var RADIO_ITEM_CLASSES = {
  primary: "outline-primary data-[state=checked]:border-primary",
  secondary: "outline-secondary data-[state=checked]:border-secondary",
  tertiary: "outline-tertiary data-[state=checked]:border-tertiary",
  action: "outline-action data-[state=checked]:border-action",
  white: "outline-body-dark data-[state=checked]:border-body-dark",
  black: "outline-body-light data-[state=checked]:border-body-light"
};
var INDICATOR_CLASSES = {
  default: {
    primary: 'after:block after:h-[14px] after:w-[14px] after:rounded-full after:content-[""] after:bg-primary',
    secondary: 'after:block after:h-[14px] after:w-[14px] after:rounded-full after:content-[""] after:bg-secondary',
    tertiary: 'after:block after:h-[14px] after:w-[14px] after:rounded-full after:content-[""] after:bg-tertiary',
    action: 'after:block after:h-[14px] after:w-[14px] after:rounded-full after:content-[""] after:bg-action',
    white: 'after:block after:h-[14px] after:w-[14px] after:rounded-full after:content-[""] after:bg-body-dark',
    black: 'after:block after:h-[14px] after:w-[14px] after:rounded-full after:content-[""] after:bg-body-light'
  },
  check: {
    primary: "bg-primary text-primary-contrast [&>svg]:w-full [&>svg]:h-full [&>svg]:rounded-full [&>svg]:p-1",
    secondary: "bg-secondary text-secondary-contrast [&>svg]:w-full [&>svg]:h-full [&>svg]:rounded-full [&>svg]:p-1",
    tertiary: "bg-tertiary text-tertiary-contrast [&>svg]:w-full [&>svg]:h-full [&>svg]:rounded-full [&>svg]:p-1",
    action: "bg-action text-action-contrast [&>svg]:w-full [&>svg]:h-full [&>svg]:rounded-full [&>svg]:p-1",
    white: "bg-body-dark text-body-light [&>svg]:w-full [&>svg]:h-full [&>svg]:rounded-full [&>svg]:p-1",
    black: "bg-body-light text-body-dark [&>svg]:w-full [&>svg]:h-full [&>svg]:rounded-full [&>svg]:p-1"
  }
};

// src/RadioGroup/RadioGroup.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var RadioGroup = ({
  name,
  label,
  options,
  value,
  onChange,
  error,
  disabled,
  className,
  groupClassName,
  required,
  dir,
  hideErrorSpace = true,
  theme = "primary",
  shape = "border",
  indicator = "default",
  ref
}) => {
  return /* @__PURE__ */ jsxs(
    RadioGroupPrimitive.Root,
    {
      name,
      value,
      onValueChange: onChange,
      required,
      disabled,
      dir,
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "text-text data-[disabled=true]:text-text/60 block pb-1",
            "data-disabled": disabled,
            children: label
          }
        ),
        /* @__PURE__ */ jsx("div", { className: twMerge("scroll-mt-36 space-y-2", groupClassName), ref, tabIndex: -1, children: options.map((op) => {
          const AsComponent = op.as || "div";
          return /* @__PURE__ */ jsxs(
            AsComponent,
            __spreadProps(__spreadValues({}, op.asProps), {
              onPointerUp: () => {
                if (!disabled && !op.disabled) {
                  onChange(op.value);
                }
              },
              className: twMerge(
                "group flex cursor-pointer items-center justify-between gap-2 p-4",
                RADIO_GROUP_CLASSES[shape][theme],
                "has-[:disabled]:hover:border-paper has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50",
                className
              ),
              children: [
                /* @__PURE__ */ jsx(
                  "label",
                  {
                    className: `w-full grow ${op.disabled || disabled ? "cursor-not-allowed" : "cursor-pointer"}`,
                    htmlFor: `${name}_${op.value}`,
                    children: op.text || op.value
                  }
                ),
                /* @__PURE__ */ jsx(
                  RadioGroupPrimitive.Item,
                  {
                    className: twMerge(
                      "border-input-border disabled:border-input-disabled peer h-[24px] w-[24px] shrink-0 rounded-full border-2 aria-checked:data-[check=true]:border-none",
                      RADIO_ITEM_CLASSES[theme]
                    ),
                    value: op.value,
                    checked: value === op.value,
                    id: `${name}_${op.value}`,
                    "data-check": indicator === "check",
                    "data-testid": `ui_radio_${name}_${op.value}`,
                    disabled: op.disabled || disabled,
                    children: /* @__PURE__ */ jsx(
                      RadioGroupPrimitive.Indicator,
                      {
                        className: twMerge(
                          "flex items-center justify-center rounded-full",
                          INDICATOR_CLASSES[indicator][theme]
                        ),
                        children: indicator === "check" && /* @__PURE__ */ jsx(SuccessIcon, {})
                      }
                    )
                  }
                )
              ]
            }),
            op.value
          );
        }) }),
        (typeof error === "string" && !!error || !hideErrorSpace) && /* @__PURE__ */ jsx(
          "p",
          {
            className: `text-error my-1 min-h-[16px] text-xs ${error ? "visible" : "text-transparent"}`,
            children: error
          }
        )
      ]
    }
  );
};
var RadioGroup_default = RadioGroup;

// src/RadioGroup/RadioGroup.types.tsx
var RADIO_GROUP_THEMES = [
  "primary",
  "secondary",
  "tertiary",
  "action",
  "white",
  "black"
];

// src/RadioGroup/RadioItem.tsx
import { twMerge as twMerge2 } from "tailwind-merge";
import { SuccessIcon as SuccessIcon2 } from "@wbk/svg";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var RadioItem = ({
  value,
  checked,
  onChange,
  disabled = false,
  indicator = "default",
  theme = "primary",
  name,
  label,
  className
}) => {
  return /* @__PURE__ */ jsxs2(
    Button_default,
    {
      shape: "outlined",
      theme,
      onClick: () => {
        if (!disabled) onChange(value);
      },
      className: twMerge2(
        "!rounded-M group flex cursor-pointer items-center justify-between gap-2 border-2 p-3",
        disabled && "cursor-not-allowed opacity-50",
        !checked && "border-opacity-20",
        className
      ),
      children: [
        label && /* @__PURE__ */ jsx2(
          Typography_default,
          {
            as: "label",
            htmlFor: `${name}_${value}`,
            className: twMerge2(
              "w-full grow cursor-pointer ltr:pr-2 rtl:pl-2",
              disabled && "cursor-not-allowed"
            ),
            variant: "body-S",
            children: label
          }
        ),
        /* @__PURE__ */ jsxs2(
          "div",
          {
            className: twMerge2(
              "flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full border-2",
              RADIO_ITEM_CLASSES[theme],
              checked && indicator === "check" && "border-none"
            ),
            "data-check": indicator === "check",
            id: `${name}_${value}`,
            children: [
              indicator === "check" && checked && /* @__PURE__ */ jsx2(
                "div",
                {
                  className: twMerge2(
                    "flex h-full w-full items-center justify-center rounded-full",
                    INDICATOR_CLASSES[indicator][theme]
                  ),
                  children: /* @__PURE__ */ jsx2(SuccessIcon2, {})
                }
              ),
              indicator === "default" && checked && /* @__PURE__ */ jsx2("div", { className: "h-2.5 w-2.5 rounded-full bg-current" })
            ]
          }
        )
      ]
    }
  );
};
var RadioItem_default = RadioItem;

export {
  RadioGroup_default,
  RADIO_GROUP_THEMES,
  RadioItem_default
};
//# sourceMappingURL=chunk-ZJF3O4P7.mjs.map